const AIRWALLEX_SDK_URL = 'https://checkout.airwallex.com/assets/elements.bundle.min.js';
const AIRWALLEX_SDK_LOAD_TIMEOUT_MS = 15000;
const AIRWALLEX_SDK_STATE_LOADING = 'loading';
const AIRWALLEX_SDK_STATE_LOADED = 'loaded';
const AIRWALLEX_SDK_STATE_FAILED = 'failed';

let sdkLoadPromise = null;
let currentCardOwner = null;
let currentCardElement = null;

function findAirwallexSdkScript() {
  return Array.from(document.getElementsByTagName('script'))
    .find((script) => script.src === AIRWALLEX_SDK_URL) || null;
}

export function loadAirwallexSdk() {
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  if (window.Airwallex) {
    sdkLoadPromise = Promise.resolve(window.Airwallex);
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existingScript = findAirwallexSdkScript();
    const script = existingScript || document.createElement('script');
    let settled = false;
    let handleLoad;
    let handleError;
    let loadTimeout = null;

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      if (loadTimeout !== null) {
        clearTimeout(loadTimeout);
        loadTimeout = null;
      }
    };
    const fail = () => {
      if (settled) {
        return;
      }

      settled = true;
      script.dataset.airwallexSdkState = AIRWALLEX_SDK_STATE_FAILED;
      cleanup();
      reject(new Error(
        'Failed to load the payment form. Please refresh the page.',
      ));
    };
    handleLoad = () => {
      if (settled) {
        return;
      }

      script.dataset.airwallexSdkState = AIRWALLEX_SDK_STATE_LOADED;
      if (!window.Airwallex) {
        fail();
        return;
      }

      settled = true;
      cleanup();
      resolve(window.Airwallex);
    };
    handleError = () => {
      fail();
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (existingScript) {
      const sdkState = script.dataset.airwallexSdkState;
      const readyState = String(script.readyState || '').toLowerCase();

      if (sdkState === AIRWALLEX_SDK_STATE_FAILED) {
        fail();
        return;
      }

      if (sdkState === AIRWALLEX_SDK_STATE_LOADED
        || readyState === 'loaded'
        || readyState === 'complete'
      ) {
        handleLoad();
        return;
      }
    } else {
      script.src = AIRWALLEX_SDK_URL;
      script.async = true;
      script.dataset.airwallexSdk = 'true';
      script.dataset.airwallexSdkState = AIRWALLEX_SDK_STATE_LOADING;
      loadTimeout = setTimeout(fail, AIRWALLEX_SDK_LOAD_TIMEOUT_MS);
      document.head.appendChild(script);
      return;
    }

    loadTimeout = setTimeout(fail, AIRWALLEX_SDK_LOAD_TIMEOUT_MS);
  });

  return sdkLoadPromise;
}

export function claimAirwallexCardElement(owner, cardElement) {
  if (!owner || !cardElement) {
    throw new Error('The credit card payment form owner is invalid.');
  }

  currentCardOwner = owner;
  currentCardElement = cardElement;
}

export function ownsAirwallexCardElement(owner, cardElement) {
  return currentCardOwner === owner && currentCardElement === cardElement;
}

export function releaseAirwallexCardElement(
  owner,
  cardElement,
  airwallex = window.Airwallex,
) {
  if (!ownsAirwallexCardElement(owner, cardElement)) {
    return false;
  }

  currentCardOwner = null;
  currentCardElement = null;

  if (airwallex && typeof airwallex.destroyElement === 'function') {
    try {
      airwallex.destroyElement('card');
    } catch (error) {
      return false;
    }
  }

  return true;
}
